// paramValidator.js

export const ParamValidator = {
    MAX_LINK_LENGTH: 2048,
    MIN_LINK_LENGTH: 10,
  
    SAFE_TEXT_PATTERN: /^[\p{L}\p{N}\p{P}\p{Zs}\p{So}]*$/u,
    SAFE_TEXT_PATTERN_EXPANDED: /^[\p{L}\p{N}\p{P}\p{Zs}\p{So}\r\n\t]*$/u, //includes line breaks and tabulations
    LINK_SYMBOLS: /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}.*$/,
    USERNAME_PATTERN: /^[A-Za-z0-9_]{4,25}$/,
    BANNED_PASSWORD_CHARS_REGEX: /[^\x21-\x7E]/,
    EMAIL_PATTERN: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
  
    isNullOrBlank(str) {
        return str == null || str.trim() === '';
    },
  
    basicSanitize(input) {
        if (input == null) return null;
        return this.stripControlChars(input.trim());
    },
  
    stripControlChars(input) {
        if (input == null) return null;
        // Keep \n, \r, and \t as harmless
        return input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // note: JS uses different regex rules
    },

    normalizeLink(link) {
        if (!link || typeof link !== 'string') return null;

        // Trim whitespace and remove invisible control characters
        link = link.trim().replace(/[\x00-\x1F\x7F]/g, '');
    
        // Empty after cleaning?
        if (!link) return null;
    
        // If the user forgot the protocol, assume HTTPS
        if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(link)) {
            link = `https://${link}`;
        }
        
        try {
            const url = new URL(link);
        
            // Normalize hostname (lowercase, strip default ports)
            url.hostname = url.hostname.toLowerCase();
            if ((url.protocol === 'http:' && url.port === '80') ||
                (url.protocol === 'https:' && url.port === '443')) {
                url.port = '';
            }
        
            // Remove trailing slash if no path beyond domain
            if (url.pathname === '/') {
                url.pathname = '';
            }
        
            // Return normalized absolute URL
            return url.toString();
        } catch (e) {
            // Invalid URL
            return null;
        }
    },
  
    isValidLink(link) {
        if (this.isNullOrBlank(link)) return false;
        if (link.length > this.MAX_LINK_LENGTH) return false;
        if (!/^https?:\/\//.test(link)) return false;
        if (!this.LINK_SYMBOLS.test(link)) return false;
    
        try {
            new URL(link);
        } catch (e) {
            return false;
        }
        return true;
    },
  
    isSafeTextNullable(text, maxLength) {
        if (this.isNullOrBlank(text)) return true;
        if (text.length > maxLength) return false;
        return this.SAFE_TEXT_PATTERN.test(text);
    },
  
    isValidText(text, maxLength) {
        if (this.isNullOrBlank(text)) return false;
        if (text.length > maxLength) return false;
        return this.SAFE_TEXT_PATTERN.test(text);
    },
  
    isSafeTextNullableExpanded(text, maxLength) {
        if (this.isNullOrBlank(text)) return true;
        if (text.length > maxLength) return false;
        return this.SAFE_TEXT_PATTERN_EXPANDED.test(text);
    },
  
    isValidTextExpanded(text, maxLength) {
        if (this.isNullOrBlank(text)) return false;
        if (text.length > maxLength) return false;
        return this.SAFE_TEXT_PATTERN_EXPANDED.test(text);
    },
  
    isValidUsername(username) {
        if (this.isNullOrBlank(username)) return false;
        return this.USERNAME_PATTERN.test(username);
    },
  
    isValidPassword(password) {
        if (this.isNullOrBlank(password)) return false;
        if (password.length > 40) return false;
        return !this.BANNED_PASSWORD_CHARS_REGEX.test(password);
    },
  
    isValidStorageFileLink(storageLink) {
        if (this.isNullOrBlank(storageLink)) return false;
        return storageLink.length < 200;
    },
  
    isValidYoutubeVideoId(videoId) {
        if (this.isNullOrBlank(videoId)) return false;
        if (typeof videoId !== 'string') return false;
        return videoId.length < 20;
    },
  
    isValidSoundCloudTrackId(trackId) {
        if (this.isNullOrBlank(trackId)) return false;
        if (typeof trackId !== 'string') return false;
        return trackId.length < 20;
    },
  
    isValidEmail(email) {
        if (this.isNullOrBlank(email)) return false;
        if (email.length < 6 || email.length > 254) return false;
        if (/[^\S\r\n\t]/.test(email)) return false; // disallow spaces
        return this.EMAIL_PATTERN.test(email);
    }
};

export class ValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = "ValidationError";
    }
}